import {
  BrandfolderHexColors,
  ButtonLooks,
  FontIcons,
  HeadingLevels,
  IconButton,
  StandardHeading
} from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, MutableRefObject, useContext, useState } from 'react';
import styled from 'styled-components';

import { ConfirmDeleteDialog } from '@components/dialogs';
import { BlockContext } from '@context/blocks';
import { DragAndDropContext } from '@context/drag-and-drop';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';
import { PublishContext } from '@context/publish';
import { ThemeContext } from '@context/theme';
import { BlockTypes } from '@enums/blocks';
import { Block } from '@typings/block';
import { deleteBlock, getMoveDownPosition, getMoveUpPosition, moveBlock } from '@utilities/block';

import { BlockEditorDrag } from './block-editor-drag';

interface BlockEditorButtonsProps extends HTMLAttributes<HTMLDivElement> {
  block: Block;
  dragImageRef?: MutableRefObject<HTMLDivElement | null>;
  edit?: boolean;
  first?: boolean;
  last?: boolean;
  only?: boolean;
  sectionHeading?: boolean;
  setEdit?: (edit: boolean) => void;
}

const UnstyledBlockEditorButtons: FunctionComponent<BlockEditorButtonsProps> = (props) => {
  const { block, dragImageRef, edit, first, only, last, sectionHeading, setEdit, ...otherProps } =
    props;
  const { key, pageKey, type } = block;

  const { blocks, setBlocks, pageBlocks } = useContext(BlockContext);
  const { dragging, reordering } = useContext(DragAndDropContext);
  const { editable } = useContext(PermissionLevelContext);
  const { preview } = useContext(PreviewContext);
  const { publishing, setPublish } = useContext(PublishContext);
  const { isLight, theme } = useContext(ThemeContext);

  const [open, setOpen] = useState(false);

  const editing = editable && !preview;

  if (!editing) return null;

  const disabled = dragging || publishing || !!reordering;
  const html = type === BlockTypes.Html;
  const section = type === BlockTypes.Section;

  return (
    <div
      className={classNames({
        'block-editor-buttons': true,
        'block-editor-buttons--dark': !isLight,
        'block-editor-buttons--section': section,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      data-testid="block-editor-buttons"
      style={{ backgroundColor: section ? theme.body.backgroundColor : undefined }}
    >
      {section && (
        <StandardHeading
          className={classNames({
            'block-editor-buttons__section-heading': true,
            'block-editor-buttons__section-heading--dark': !isLight
          })}
          level={HeadingLevels.h6}
        >
          Section
        </StandardHeading>
      )}
      {!sectionHeading && (
        <>
          {!only && (
            <BlockEditorDrag block={block} dragImageRef={dragImageRef} edit={edit}>
              <IconButton
                className={classNames({
                  'block-editor-buttons__drag': true,
                  'block-editor-buttons__drag--disabled':
                    dragging || edit || publishing || !!(reordering && reordering.key !== key)
                })}
                disabled={disabled || edit}
                icon={FontIcons.DragIndicator}
                label="Drag to reorder"
                look={ButtonLooks.Tertiary}
              />
            </BlockEditorDrag>
          )}
          {!first && !only && (
            <IconButton
              disabled={disabled}
              icon={FontIcons.CaretUp}
              label="Move block up"
              look={ButtonLooks.Tertiary}
              onClick={() => {
                const { parentKey: pK, position } = getMoveUpPosition(block, pageBlocks);
                setBlocks([...moveBlock({ ...block, parentKey: pK, position }, blocks, 'up')]);
                setPublish(true);
              }}
            />
          )}
          {!last && !only && (
            <IconButton
              disabled={disabled}
              icon={FontIcons.CaretDown}
              label="Move block down"
              look={ButtonLooks.Tertiary}
              onClick={() => {
                const { parentKey: pK, position } = getMoveDownPosition(block, pageBlocks);
                setBlocks([...moveBlock({ ...block, parentKey: pK, position }, blocks, 'down')]);
                setPublish(true);
              }}
            />
          )}
        </>
      )}
      {html && (
        <IconButton
          disabled={disabled}
          icon={edit ? FontIcons.Show : FontIcons.Edit}
          label="Toggle code view"
          look={ButtonLooks.Tertiary}
          onClick={() => {
            if (setEdit) {
              setEdit(!edit);
            }
          }}
        />
      )}
      {!sectionHeading && (
        <>
          <IconButton
            disabled={disabled}
            icon={FontIcons.Trash}
            label="Delete block"
            look={ButtonLooks.Tertiary}
            onClick={() => {
              setOpen(true);
            }}
          />
          <ConfirmDeleteDialog
            block={block}
            id={`confirm-delete-${key}`}
            onCancel={() => setOpen(false)}
            onConfirm={() => {
              setBlocks([...deleteBlock(block, blocks, pageKey)]);
              setPublish(true);
              setOpen(false);
            }}
            open={open}
            setOpen={setOpen}
          />
        </>
      )}
    </div>
  );
};

const StyledBlockEditorButtons = styled(UnstyledBlockEditorButtons)`
  display: flex;
  justify-content: flex-end;
  position: relative;
  top: -4px;

  .bf-button__icon {
    height: 32px;
    margin-left: 4px;
    min-height: 0;
    padding: 0;
    width: 32px;

    .bf-icon__font {
      font-size: 12px;
    }
  }

  .block-editor-buttons__section-heading--dark {
    color: ${BrandfolderHexColors.White};
  }

  .block-editor-buttons__drag {
    cursor: grab;
    margin-left: 0;

    &.block-editor-buttons__drag--disabled {
      cursor: not-allowed;
    }

    .bf-icon__font {
      font-size: 16px;
    }
  }
`;

export const BlockEditorButtons: FunctionComponent<BlockEditorButtonsProps> = (props) => {
  return <StyledBlockEditorButtons {...props} />;
};
